// @givmo/board — "signing in from the invitation joins at once" (D-CBG-18 answer 3), per tab.
//
// Jonathan: "3) agree" — a visitor who signs in FROM the invitation has already chosen to accept, so
// the claim follows the sign-in with no second tap. Opening an invitation spends nothing, so the
// landing claims only when the visitor chose a way in on it: that choice is recorded here, in this
// tab's `sessionStorage`, and consumed on the landing's first signed-in render.
//
// NEVER A URL FLAG. A flag in the address would ride a forwarded link and claim on someone else's
// sign-in; this marker can't leave the tab. It holds the page's token, which is already in this tab's
// address, so it reveals nothing the tab doesn't hold. Every access is guarded: with storage blocked
// the visitor meets "Join the board" — one tap — instead.
//
// A SIGN-IN THAT ENDED WHILE JOINING (copy round 2, D-CBG-18) is kept here the same way. The landing
// signs the tab out and the console remounts it signed out, so the reason travels in this tab's
// storage: the signed-out invitation says "Your sign-in ended. Sign in again to join." above its ways
// in, until a way in is picked or the tab signs in again. With storage blocked the line is simply
// not said, as before it existed.
//
// "USE ANOTHER GIVMO ACCOUNT" (CBG-SEC2-L4, D-CBG-23; frames a1 and a2) is kept the same way too. An
// account another organization's sign-in lock binds can't join, so the landing signs it out on the
// person's word,
// and the signed-out invitation says why it's back at the ways in: the hint above them, the ways in
// without the organization door, and "Create new account" under them, until the tab signs in again.
// "CREATE NEW ACCOUNT" from the signed-in card (a1 → a2c) rides a one-shot marker of its own: the
// landing signs out back to itself, and the page the console remounts signed out opens the create
// form. Opening the form straight after the sign-out would race the form's own guard, which turns a
// still-signed-in visitor back before the console has seen the sign-out.

//
// WHOSE INVITATION IT WAS (CBG-FIX106, the FIX104 copy page's row 4.10) is remembered the same way:
// how many committees its preview named — a count, never a name, 0 for the board's — so the refusal
// page this tab may meet later, signed out (after "Switch account" remounts it), says "Sign in to
// open the committee." where the board's says "…your board.". With storage blocked, or a refusal this
// tab never saw the preview of — a refused preview names nothing — the page can't know whose it was,
// and says the line true of both (review-233's F1).

const KEY = 'board-director-join';
const SIGN_IN_ENDED_KEY = 'board-director-sign-in-ended';
const ANOTHER_ACCOUNT_KEY = 'board-director-another-account';
const CREATE_ANOTHER_KEY = 'board-director-create-another';
const INVITATION_COMMITTEES_KEY = 'board-director-invitation-committees';

/** The visitor chose a way in on the invitation whose token is `token`. */
export function markJoinIntent(token: string): void {
  try {
    window.sessionStorage.setItem(KEY, token);
  } catch {
    // Storage refused: the landing offers its one tap instead.
  }
}

/** Whether this tab chose to join THIS invitation; the marker is consumed either way. */
export function takeJoinIntent(token: string): boolean {
  try {
    const marked = window.sessionStorage.getItem(KEY);
    if (marked === null) return false;
    window.sessionStorage.removeItem(KEY);
    return marked === token;
  } catch {
    return false;
  }
}

/** Forget the choice ("Use another account", or a refusal that ends the visit). */
export function clearJoinIntent(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/** The sign-in ended while this tab was joining the invitation whose token is `token`. */
export function markSignInEnded(token: string): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_ENDED_KEY, token);
  } catch {
    // Storage refused: the signed-out invitation shows without the line.
  }
}

/** Whether this tab's sign-in ended while it was joining THIS invitation. Read, not consumed. */
export function signInEndedOn(token: string): boolean {
  try {
    return window.sessionStorage.getItem(SIGN_IN_ENDED_KEY) === token;
  } catch {
    return false;
  }
}

/** Forget it: a way in was picked, or the tab is signed in again. */
export function clearSignInEnded(): void {
  try {
    window.sessionStorage.removeItem(SIGN_IN_ENDED_KEY);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/** This tab chose "Use another Givmo account" on the invitation whose token is `token`. */
export function markAnotherAccount(token: string): void {
  try {
    window.sessionStorage.setItem(ANOTHER_ACCOUNT_KEY, token);
  } catch {
    // Storage refused: the signed-out invitation shows its ordinary ways in.
  }
}

/** Whether this tab is looking for another Givmo account for THIS invitation. Read, not consumed. */
export function anotherAccountOn(token: string): boolean {
  try {
    return window.sessionStorage.getItem(ANOTHER_ACCOUNT_KEY) === token;
  } catch {
    return false;
  }
}

/** Forget it: the tab is signed in again. */
export function clearAnotherAccount(): void {
  try {
    window.sessionStorage.removeItem(ANOTHER_ACCOUNT_KEY);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/** This tab chose "Create new account" on the invitation whose token is `token`, signed in. */
export function markCreateAnotherAccount(token: string): void {
  try {
    window.sessionStorage.setItem(CREATE_ANOTHER_KEY, token);
  } catch {
    // Storage refused: the signed-out invitation shows its ordinary ways in.
  }
}

/** Whether this tab chose it for THIS invitation; the marker is consumed either way. */
export function takeCreateAnotherAccount(token: string): boolean {
  try {
    const marked = window.sessionStorage.getItem(CREATE_ANOTHER_KEY);
    if (marked === null) return false;
    window.sessionStorage.removeItem(CREATE_ANOTHER_KEY);
    return marked === token;
  } catch {
    return false;
  }
}

/** The invitation whose token is `token` is to `count` committees, 0 for the board's (its preview
 *  said so). */
export function markInvitationCommittees(token: string, count: number): void {
  try {
    window.sessionStorage.setItem(INVITATION_COMMITTEES_KEY, `${count}:${token}`);
  } catch {
    // Storage refused: a refusal page says the line true of both.
  }
}

/** How many committees THIS invitation's preview named in this tab — 0, the board's — or null when
 *  this tab never read it. Read, not consumed. */
export function invitationCommitteesOn(token: string): number | null {
  try {
    const marked = window.sessionStorage.getItem(INVITATION_COMMITTEES_KEY);
    if (marked === null) return null;
    const at = marked.indexOf(':');
    const count = Number(marked.slice(0, at));
    return marked.slice(at + 1) === token && Number.isInteger(count) && count >= 0 ? count : null;
  } catch {
    return null;
  }
}
